<template>
	<v-main data-tour-scope="brand-import">
		<v-container fluid>
			<v-card width="100%" outlined elevation-0>
				<v-toolbar flat class="border_bottom" shaped>
					<v-toolbar-title>Import da marchi</v-toolbar-title>
					<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()">
							<v-icon left>mdi-school-outline</v-icon>
							Tutorial guidato
						</v-btn>
					<v-spacer></v-spacer>
					<v-btn text @click="aggiornaTutto"><v-icon>mdi-refresh</v-icon></v-btn>
				</v-toolbar>

				<v-card-text>
					<v-alert v-if="openaiAttivo === false" type="warning" prominent border="left" data-tour="openai">
						<div class="d-flex align-center flex-wrap">
							<div class="mr-4">
								<strong>Per utilizzare Import da marchi devi aver configurato OpenAI.</strong>
								<div>Serve a generare le regole con cui il sistema legge i siti delle marche. Si attiva con la tua chiave OpenAI in Impostazioni &gt; AI.</div>
							</div>
							<v-spacer></v-spacer>
							<v-btn color="warning" depressed to="/setting/ai-social" class="mt-2 mt-md-0">Configura OpenAI</v-btn>
						</div>
					</v-alert>
					<v-alert type="info" dense text dismissible>
						Il sistema legge la sitemap del sito della marca, estrae i prodotti e li mette qui: nel catalogo entrano solo quelli che approvi. Usalo per le marche di cui sei rivenditore autorizzato.
					</v-alert>

					<v-row>
						<v-col cols="12" md="4" lg="3">
							<brand-list :brands="marche" :selected-id="selezionata" data-tour="brand-list" @select="seleziona"></brand-list>
						</v-col>
						<v-col cols="12" md="8" lg="9">
							<template v-if="marca">
								<brand-panel
									:brand="marca"
									:job="jobMarca"
									:openai-active="openaiAttivo !== false"
									class="mb-4"
									data-tour="brand-panel"
									@configure="apriImpostazioni"
									@generate="avvia('generate_rules')"
									@scan="avvia('scan')"
									@scan-fresh="avvia('scan', { fresh: true })"
									@pages-changed="paginePerVerifica"
									@message="mostra"
									@cancel="chiediAnnulla"
								></brand-panel>
								<products-table
									ref="prodotti"
									:brand-id="marca._id"
									:settings="settings"
									:per-page-options="perPageOptions"
									data-tour="products"
									:refresh-key="refreshKey"
									@filter="cambiaFiltri"
									@options="settings.options = $event"
									@search="settings.search = $event"
									@open="apriProdotto"
									@catalog="apriCatalogo"
									@approve="apriApprovazione"
									@approve-filtered="apriApprovazioneFiltrati"
									@job="segui"
									@error="mostra($event, 'error')"
									@info="mostra($event, 'success')"
								></products-table>
							</template>
							<v-card v-else outlined class="pa-8 text-center grey--text">Scegli una marca a sinistra per iniziare.</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-container>

		<!-- conferma breve: fermare un'operazione in corso non si puo' annullare -->
		<v-dialog v-model="confermaAnnulla.aperta" max-width="460">
			<v-card>
				<v-card-title>Fermare l'operazione in corso?</v-card-title>
				<v-card-text class="text-left">L'operazione viene interrotta: quello che ha gia' fatto resta, per il resto andra' avviata di nuovo.</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="confermaAnnulla.aperta = false">No, continua</v-btn>
					<v-btn color="error" depressed @click="annulla">Ferma</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- conferma dell'importazione: categorie e IVA con cui i prodotti entrano in catalogo -->
		<approve-dialog v-model="dialogApprova" :ids="daApprovare" :filtro="filtroApprovazione" :totale="totaleApprovazione" :brand-id="selezionata" @done="onApprovazione"></approve-dialog>

		<TourOverlay :tour="tour"></TourOverlay>
		<v-snackbar v-model="snack.visibile" :color="snack.colore" timeout="5000">{{ snack.testo }}</v-snackbar>
	</v-main>
</template>

<script>
import _ from 'lodash';
import BrandList from '../../components/brandimport/BrandList.vue';
import BrandPanel from '../../components/brandimport/BrandPanel.vue';
import ProductsTable from '../../components/brandimport/ProductsTable.vue';
import ApproveDialog from '../../components/brandimport/ApproveDialog.vue';
import { attivo, testoErrore, TIPI_JOB, STATI_JOB } from '../../components/brandimport/testi';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { listRouteState } from '@/components/core/list/listRouteState';
import { openWithReturn } from '@/components/core/pageReturn';
import { createBrandImportTourSteps } from './brand-import-tour';

const POLLING_MS = 5000;
const LIST_SIZE = [25, 50, 100];
const DA_VALUTARE = 'new,updated';

export default {
	name: 'BrandImport',
	components: { BrandList, BrandPanel, ProductsTable, ApproveDialog, TourOverlay },
	// marca scelta, scheda dei prodotti, ricerca, filtro doppioni e pagina stanno nell'URL:
	// refresh e ritorno dalle schede ripartono da li'
	mixins: [listRouteState({ filters: { brand: 'string', status: 'string', duplicates: 'boolean' }, perPageOptions: LIST_SIZE, fetch: 'caricaProdotti' })],
	data() {
		return {
			tour: useTour(createBrandImportTourSteps()),
			marche: [],
			openaiAttivo: null,
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { brand: '', status: DA_VALUTARE, duplicates: false },
				options: { sortBy: [], sortDesc: [], page: 1, itemsPerPage: 25 },
			}),
			perPageOptions: LIST_SIZE,
			confermaAnnulla: { aperta: false, jobId: null },
			// job seguiti in tempo reale (per id): scansioni, regole, riscritture, importazioni
			jobs: {},
			timer: null,
			refreshKey: 0,
			dialogApprova: false,
			// "Importa tutti": filtri della lista e quanti prodotti mostra (null = import dei singoli)
			filtroApprovazione: null,
			totaleApprovazione: 0,
			daApprovare: [],
			snack: { visibile: false, testo: '', colore: 'success' },
		};
	},
	computed: {
		selezionata() {
			return this.settings.filters.brand || null;
		},
		marca() {
			return this.marche.find(b => b._id === this.selezionata) || null;
		},
		jobMarca() {
			if (!this.marca) return null;
			// una riscrittura in blocco dura a lungo: avanzamento e Annulla nel pannello, come una scansione
			const inBlocco = j => j.type === 'rewrite' && ((j.params && j.params.productIds) || []).length > 1;
			const seguiti = Object.values(this.jobs).filter(j => j.brand === this.marca._id && (inBlocco(j) || !['approve', 'rewrite', 'translate', 'snapshot'].includes(j.type)));
			return _.maxBy(seguiti, j => new Date(j.created_at).getTime()) || this.marca.lastJob;
		},
	},
	sockets: {
		// il backend lo invia quando un job arriva in fondo (watchdog brandimport)
		MESSAGE_BRAND_IMPORT(raw) {
			let data;
			try {
				data = typeof raw === 'string' ? JSON.parse(raw) : raw;
			} catch (e) {
				return;
			}
			if (data && data.jobId) this.aggiornaJob(data.jobId);
		},
	},
	async created() {
		await Promise.all([this.caricaMarche(), this.verificaOpenai()]);
		this.timer = setInterval(this.polling, POLLING_MS);
		// la tabella dei prodotti esiste solo con la marca caricata: il primo caricamento parte ora
		this.listLoad();
	},
	beforeDestroy() {
		clearInterval(this.timer);
		this.tour.stop();
	},
	methods: {
		mostra(testo, colore = 'success') {
			this.snack = { visibile: true, testo, colore };
		},
		async verificaOpenai() {
			try {
				const res = await this.$store.dispatch('services/getServiceFetchActive', ['openai']);
				const openai = _.find(res, s => s.service === 'openai');
				this.openaiAttivo = !!(openai && openai.active);
			} catch (e) {
				this.openaiAttivo = null; // il backend risponde comunque openai_not_configured
			}
		},
		async caricaMarche() {
			const res = await this.$store.dispatch('brandimport/fetchBrands');
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.marche = res.data;
			// riprende a seguire i job ancora in corso (es. dopo un refresh della pagina)
			this.marche.forEach(b => {
				if (attivo(b.lastJob)) this.$set(this.jobs, b.lastJob._id, { ...b.lastJob, brand: b._id });
			});
		},
		aggiornaTutto() {
			this.refreshKey++;
			this.caricaMarche();
		},
		// nuova marca: si riparte dai prodotti da valutare (il mixin torna a pagina 1, aggiorna l'URL e ricarica)
		seleziona(id) {
			if (id === this.settings.filters.brand) return;
			this.settings.filters = { ...this.settings.filters, brand: id, status: DA_VALUTARE };
		},
		cambiaFiltri(patch) {
			this.settings.filters = { ...this.settings.filters, ...patch };
		},
		// fetch del mixin: la tabella carica i prodotti con lo stato della lista
		// (scelta la prima marca la tabella non esiste ancora: si carica appena montata)
		caricaProdotti() {
			const tabella = this.$refs.prodotti;
			if (tabella) return tabella.carica();
			this.$nextTick(() => {
				if (this.$refs.prodotti) this.$refs.prodotti.carica();
			});
			return undefined;
		},
		apriImpostazioni() {
			if (this.marca) this.$router.push(`/brand-import/brand/${this.marca._id}`);
		},
		apriProdotto(id) {
			this.$router.push(`/brand-import/product/${id}`);
		},
		// prodotto del catalogo (importato o con lo stesso EAN/SKU): la freccia riporta qui
		apriCatalogo(itemId) {
			openWithReturn(this, { path: `/items/${itemId}` }, { label: 'Torna a Import da marchi' });
		},
		segui(job) {
			if (job && job._id) this.$set(this.jobs, job._id, { ...job, brand: String(job.brand) });
		},
		// extra.fresh: scansione da capo invece di riprendere da dove si era fermata
		async avvia(type, extra = {}) {
			const res = await this.$store.dispatch('brandimport/createJob', { brandId: this.selezionata, type, ...extra });
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.segui(res.data);
			this.mostra(type === 'scan' ? 'Scansione avviata: le pagine vengono lette lentamente, puoi chiudere questa pagina.' : 'Generazione delle regole avviata.');
		},
		// Escludi / Ignora / Includi aggiornano la marca; Riprova avvia anche una riscansione da seguire
		paginePerVerifica(job) {
			if (job) this.segui(job);
			this.caricaMarche();
		},
		chiediAnnulla(jobId) {
			this.confermaAnnulla = { aperta: true, jobId };
		},
		async annulla() {
			const jobId = this.confermaAnnulla.jobId;
			this.confermaAnnulla = { aperta: false, jobId: null };
			if (!jobId) return;
			const res = await this.$store.dispatch('brandimport/cancelJob', jobId);
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.segui(res.data);
		},
		apriApprovazione(ids) {
			this.filtroApprovazione = null;
			this.daApprovare = ids;
			this.dialogApprova = true;
		},
		apriApprovazioneFiltrati(totale) {
			const f = this.settings.filters;
			this.filtroApprovazione = { status: f.status, duplicates: !!f.duplicates, q: this.settings.search || '' };
			this.totaleApprovazione = totale;
			this.daApprovare = [];
			this.dialogApprova = true;
		},
		onApprovazione(job) {
			this.segui(job);
			this.refreshKey++;
			this.mostra('Importazione avviata: riceverai una notifica al termine.');
		},
		async aggiornaJob(jobId) {
			const res = await this.$store.dispatch('brandimport/fetchJob', jobId);
			if (res.error || !res.data) return;
			const prima = this.jobs[jobId];
			const job = { ...res.data, brand: String(res.data.brand) };
			this.$set(this.jobs, jobId, job);
			if (prima && attivo(prima) && !attivo(job)) {
				const stato = STATI_JOB[job.status] || { testo: job.status };
				this.mostra(`${TIPI_JOB[job.type] || job.type}: ${stato.testo.toLowerCase()}`, job.status === 'done' ? 'success' : 'error');
				this.refreshKey++;
				this.caricaMarche();
			}
		},
		async polling() {
			const inCorso = Object.values(this.jobs).filter(attivo);
			for (const job of inCorso) {
				await this.aggiornaJob(job._id);
			}
		},
	},
};
</script>
